import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { 
  Shield, Heart, Lightbulb, Leaf, DollarSign,
  Target, Eye
} from 'lucide-react';

const TEAM_IMG = "https://images.unsplash.com/photo-1758873269317-51888e824b28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtJTIwbWVldGluZyUyMGRpdmVyc2V8ZW58MHx8fHwxNzcxNDMwOTU4fDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t, language } = useLanguage();

  const values = [
    { key: 'integrity', icon: Shield, color: 'from-blue-500 to-cyan-500' },
    { key: 'empathy', icon: Heart, color: 'from-pink-500 to-rose-500' },
    { key: 'innovation', icon: Lightbulb, color: 'from-yellow-500 to-orange-500' },
    { key: 'sustainability', icon: Leaf, color: 'from-green-500 to-emerald-500' },
    { key: 'affordability', icon: DollarSign, color: 'from-violet-500 to-purple-500' },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div data-testid="about-page" className="circuit-bg">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-primary font-medium mb-2 block">{t('about.title')}</span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">{t('about.subtitle')}</h1>
            <p className="text-lg text-muted-foreground">{t('about.description')}</p>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ delay: 0.2 }}
            className="relative rounded-3xl overflow-hidden mb-16"
          >
            <img 
              src={TEAM_IMG}
              alt="Team"
              className="w-full h-64 md:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="text-lg md:text-xl font-medium text-foreground">
                "{t('about.philosophy')}"
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Vision */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Card className="h-full border-primary/20 bg-card/50">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Eye className="h-7 w-7 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold">{t('about.vision')}</h2>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">
                    {t('about.visionText')}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full border-primary/20 bg-card/50">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <Target className="h-7 w-7 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold">{t('about.mission')}</h2>
                  </div>
                  <ul className="space-y-3">
                    {t('about.missionItems').map((item, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <span className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center flex-shrink-0 text-sm font-medium">
                          {index + 1}
                        </span>
                        <span className="text-muted-foreground text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('about.values')}</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.key}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  data-testid={`value-card-${value.key}`}
                  className="group h-full border border-border hover:border-primary/50 transition-all duration-300"
                >
                  <CardContent className="p-6 text-center">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${value.color} flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                      <value.icon className="h-7 w-7 text-white" />
                    </div>
                    <h3 className="font-semibold mb-2">
                      {t(`about.valuesItems.${value.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(`about.valuesItems.${value.key}.desc`)}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
